import { TrackingEventList } from 'portal-sdk';
import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { sendHubspotEvent, usePortalUser } from 'vet-bones/utils/portal';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { getDataSourceTableKey } from 'src/constants/dataSourceTable';
import { CompetencyQuestion } from 'src/constants/voicebox';
import { useCreateProjectState } from 'src/containers/CreateProjectOverlay/hooks/useCreateProjectState';
import {
  FileToUpload,
  VirtualGraphSourceConfig,
} from 'src/containers/CreateProjectOverlay/types';
import {
  ProjectVirtualGraph,
  ProjectVirtualGraphOptions,
  defaultProjectFileUploadOptions,
} from 'src/store/state/projects';
import { uiSlice } from 'src/store/ui/ui.slice';
import { FileCreationMetadata } from 'src/types/fileUpload';
import { createNewCloudProject } from 'src/utils/cloud/createNewCloudProject';
import { generateDateRandomId, generateDateTimeId } from 'src/utils/generateId';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useNavigate } from 'src/utils/hooks/useNavigate';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { getProjectRoute } from 'src/utils/routes/getProjectRoute';

export type CreateProjectOptions = {
  /** If true, stores pendingAutoGenerateModel flag and keeps overlay open */
  autoGenerateModel?: boolean;
  /** If true, stores pendingAutoGenerateMockData flag and keeps overlay open */
  autoGenerateMockData?: boolean;
};

function mergeVgOptions(
  config: VirtualGraphSourceConfig
): ProjectVirtualGraphOptions {
  const merged: ProjectVirtualGraphOptions = { ...config.virtualGraphOptions };
  config.advancedVgOptions.forEach(({ property, value }) => {
    if (property.trim()) {
      (merged as Record<string, string | boolean | undefined>)[property] =
        value || undefined;
    }
  });
  return merged;
}

function buildVirtualGraphsToCreate(
  virtualGraphConfigs: VirtualGraphSourceConfig[],
  connectionId: string | null
): Array<Omit<ProjectVirtualGraph, 'dateLastUpdated'>> {
  const vgs: Array<Omit<ProjectVirtualGraph, 'dateLastUpdated'>> = [];

  virtualGraphConfigs.forEach((config) => {
    const mergedOptions = mergeVgOptions(config);

    config.checkedTables.forEach((table) => {
      const metadata = config.tableMetadata[getDataSourceTableKey(table)];
      const resourceName = metadata?.editedName || table.name;
      const fields = metadata?.fields
        ? metadata.fields.map((field) => ({
            ...field,
            label: metadata.editedFieldLabels?.[field.value] ?? field.label,
          }))
        : [];

      vgs.push({
        id: generateDateRandomId(),
        name: resourceName,
        dataSourceId: config.dataSource.id,
        dataSourceType: config.dataSource.type,
        dataSourceEndpoint: config.dataSource.options?.['jdbc.url'] || '',
        connectionId,
        from: {
          name: table.name,
          text: table.name,
          value: table.name,
          catalog: table.catalog,
          schema: table.schema,
        },
        fields,
        previewRows: metadata?.previewRows || [],
        allResultsIncluded: false,
        options: mergedOptions,
      });
    });

    config.customSqlQueries.forEach((query) => {
      vgs.push({
        id: generateDateRandomId(),
        name: query.name,
        dataSourceId: config.dataSource.id,
        dataSourceType: config.dataSource.type,
        dataSourceEndpoint: config.dataSource.options?.['jdbc.url'] || '',
        connectionId,
        from: query.query,
        fields: query.fields || [],
        previewRows: query.previewRows || [],
        allResultsIncluded: false,
        options: mergedOptions,
      });
    });
  });

  return vgs;
}

async function buildFilesToUpload(
  selectedFiles: File[],
  fileContents: Record<number, string>,
  fileMetadata: Record<number, FileCreationMetadata>
): Promise<FileToUpload[]> {
  if (!selectedFiles.length) {
    return [];
  }

  return selectedFiles.map((file, idx) => {
    const metadata = fileMetadata[idx];
    const isPartial = metadata?.isPartial || false;
    const fileOpts = metadata?.fileOptions || defaultProjectFileUploadOptions;

    return {
      name: metadata?.fileName || file.name.replace(/\.csv$/i, ''),
      path: file.name,
      contents: fileContents[idx],
      options: fileOpts,
      fields: metadata?.fields || [],
      id: generateDateRandomId(),
      partial: isPartial,
    };
  });
}

export const useCreateProjectBase = () => {
  const {
    name: stateName,
    description: stateDescription,
    competencyQuestions: stateCompetencyQuestions,
    selectedFiles,
    fileContents,
    fileMetadata,
    virtualGraphConfigs,
    resetState,
  } = useCreateProjectState();

  const isMounted = useIsMounted();
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();
  const defaultConnection = useDefaultPortalConnection();
  const portalUser = usePortalUser();
  const isCloudStorageEnabled = portalUser.data?.is_designer_storage_enabled;

  const createProject = React.useCallback(
    async (options: CreateProjectOptions = {}) => {
      const { autoGenerateModel = false, autoGenerateMockData = false } =
        options;
      const name = stateName.trim();
      if (!name) {
        return;
      }

      const description = stateDescription.trim();
      const competencyQuestions = stateCompetencyQuestions.reduce<
        CompetencyQuestion[]
      >((acc, question) => {
        const trimmed = question.question.trim();
        if (trimmed) {
          acc.push({ id: question.id, question: trimmed });
        }
        return acc;
      }, []);

      let filesToUpload: FileToUpload[] = [];
      try {
        filesToUpload = await buildFilesToUpload(
          selectedFiles,
          fileContents,
          fileMetadata
        );
      } catch (error) {
        console.error('Failed to read CSV files:', error);
      }

      const virtualGraphsToCreate = buildVirtualGraphsToCreate(
        virtualGraphConfigs,
        defaultConnection?.id || null
      );

      const id = generateDateTimeId();
      const modelId = uuid();
      const dateCreated = Date.now();

      let projectId = id;
      if (isCloudStorageEnabled) {
        const cloudProjectId = await createNewCloudProject(
          { name, description, competencyQuestions },
          defaultConnection?.id
        );

        if (!cloudProjectId || !isMounted.current) {
          return;
        }
        projectId = cloudProjectId;

        if (filesToUpload.length) {
          dispatch(
            projectsActionCreators.addedPendingFileUploads(
              cloudProjectId,
              filesToUpload
            )
          );
        }

        if (virtualGraphsToCreate.length) {
          dispatch(
            projectsActionCreators.addedPendingVirtualGraphs(
              cloudProjectId,
              virtualGraphsToCreate
            )
          );
        }
      } else {
        dispatch(
          projectsActionCreators.addedProject({
            id,
            connectionId: defaultConnection?.id,
            modelId,
            name,
            description,
            competencyQuestions,
            dateCreated,
          })
        );

        if (filesToUpload.length) {
          dispatch(
            projectsActionCreators.addedFileUploadsToProject(id, filesToUpload)
          );
        }

        if (virtualGraphsToCreate.length) {
          dispatch(
            projectsActionCreators.addedVirtualGraphsToProject(
              id,
              ...virtualGraphsToCreate
            )
          );
        }
      }

      const hasResourcesToGenerate =
        filesToUpload.length || virtualGraphsToCreate.length;
      if (autoGenerateModel && hasResourcesToGenerate) {
        const fileResourceIds = filesToUpload.map((file) => file.id);
        const vgResourceIds = virtualGraphsToCreate.map((vg) => vg.id);
        const resourceIds = [...fileResourceIds, ...vgResourceIds];
        dispatch(
          projectsActionCreators.addedPendingAutoGenerateModel(
            projectId,
            resourceIds
          )
        );
      } else if (autoGenerateMockData && !hasResourcesToGenerate) {
        dispatch(
          projectsActionCreators.addedPendingAutoGenerateMockData(projectId)
        );
      } else {
        dispatch(
          uiSlice.actions.toggledCreateProjectOverlay({ isOpen: false })
        );
        resetState();
      }

      sendHubspotEvent(TrackingEventList.DESIGNER_CREATE_PROJECT);
      navigate(getProjectRoute(projectId));
    },
    [
      defaultConnection,
      dispatch,
      fileContents,
      fileMetadata,
      isCloudStorageEnabled,
      isMounted,
      navigate,
      resetState,
      selectedFiles,
      stateCompetencyQuestions,
      stateDescription,
      stateName,
      virtualGraphConfigs,
    ]
  );

  return createProject;
};
